import type { HomepageContent } from './types';

const GITHUB_URL = 'https://github.com/ATTCKDigital/smith';
const ATTCK_URL = 'https://attck.com';

const content: HomepageContent = {
  meta: {
    title: 'Smith — Claude Code Infrastructure for Agencies',
    description:
      'Claude Code infrastructure for agencies. Persistent memory, self-improving workflows, and production-grade tooling from ATTCK.',
    siteUrl: 'https://smith.attck.com',
    ogType: 'website',
  },

  hero: {
    logoAlt: 'Smith',
    headline: [{ text: 'Claude Code Infrastructure for ' }, { text: 'Agencies', accent: true }],
    subheadline:
      'Smith gives Claude Code persistent memory, self-learning, & production-grade design & development workflows.',
    cta: {
      label: 'View on GitHub',
      href: GITHUB_URL,
      external: true,
    },
  },

  valueProposition: {
    eyebrow: 'What is Smith',
    description:
      "Smith is not another skill pack. It's the substrate that makes your skills work. A toolkit of hooks, memory, & workflows that turns Claude Code from a stateless assistant into a persistent development partner.",
    benefits: [
      {
        title: 'Persistent Memory',
        description:
          'Session memory, spec changes, & learned patterns stored for lower token usage & improved effeciency.',
      },
      {
        title: 'Self-Improving',
        description:
          'Smith learns from recursive forced-feedback workflow executions & applies lessons to future work.',
      },
      {
        title: 'Built by ATTCK',
        description:
          'Every feature comes from real-world use while shipping client work at <a href="https://attck.com">ATTCK</a>.',
      },
    ],
  },

  featureList: {
    sectionHeading: 'What Smith does',
    roadmapBadgeLabel: 'Coming soon',
    categories: [
      {
        id: 'core',
        name: 'Core',
        tagline: 'Memory, security, efficiency',
        features: [
          {
            name: 'The Vault',
            description: 'Persistent memory bus for stateless sessions and sub-agents.',
            bullets: [
              'Timestamped session logs tracks file change, decisions, & actions',
              'Sub-agent activity persists by type and ID for cross-invocation continuity',
              '`/smith-vault list` to browse sessions, agents, queue, & status',
            ],
          },
          {
            name: 'The Bank',
            description: 'Save ideas mid-conversation for processing later.',
            bullets: [
              '`/smith-bank add` to "bank this idea" for later',
              'Auto-identify system-specific impact',
              'Banked items include origin context, requirements, & questions',
              'Process banked ideas into full workflows',
            ],
          },
          {
            name: 'The Ledger',
            description: 'Self-improving agent memory that learns from every workflow execution.',
            bullets: [
              'Extracts patterns & antipatterns from successful & failed workflows automatically',
              'Applies accumulated knowledge to future builds, bugfixes, & audits',
              'Auto-dream with `/smith-ledger reconcile` merges duplicates & prunes stale patterns across constitution.md, CLAUDE.md, & MEMORY.md',
            ],
          },
          {
            name: 'Security Guards',
            description: 'Blocks dangerous commands and protects sensitive files.',
            bullets: [
              'Protected file patterns for .env files, keys, certificates, config & lock files',
              'Auto-approve safe, read-only commands & configurable allowlist',
            ],
          },
          {
            name: 'Intent Detection',
            description: 'Classifies messages as questions vs commands before processing.',
            bullets: [
              "Questions don't trigger presumptive actions",
              'Suggests actionable follow-up options',
              'Leverages task-specific model-routing',
            ],
          },
          {
            name: 'Auto Compaction',
            description: 'Proactive warnings when session context approaches limits.',
            bullets: [
              'Large file warning limits excessive token use',
              'Image routing to sub-agents',
              'Events logged to Vault for pattern analysis',
            ],
          },
          {
            name: 'Model Routing',
            description: 'Delegates sub-agents using the cheapest model based on the task.',
            bullets: [
              'Automatic classification during workflows',
              'Haiku for exploration, Sonnet for docs and review, Opus for implementation',
              'Only triggers if cost-savings apply',
            ],
          },
          {
            name: 'Auto-Formatting',
            description: 'Clean, consistent, & documented code for every file-type.',
            bullets: [
              'Enforced on all Write and Edit actions',
              'Non-blocking, never interrupt the workflow',
              'Prettier for JS, CSS, & HTML.',
              'Ruff for Python linting and formatting',
            ],
          },
          {
            name: 'Command Reference',
            description: 'Built-in help system with detailed command usage guides.',
            bullets: [
              '`/smith-help` displays commands by category',
              'Contextual syntax, flags, & examples tooltips',
              'MCP endpoint & command specs (soon)',
            ],
          },
        ],
      },
      {
        id: 'development',
        name: 'Development',
        tagline: 'Process, tasks, audits',
        features: [
          {
            name: 'Speckit-Driven',
            description: 'End-to-end requirements to deployment workflows.',
            bullets: [
              'Interactive Requirements Gathering',
              'Concurrent workflows with Git Worktrees',
              '`/smith-new`, `/smith-bugfix`, & `/smith-debug` workflows',
              '`/smith-explore` for pre-change impact analysis with parallel sub-agent investigation',
            ],
          },
          {
            name: 'Spec Drift Detection',
            description: 'Diff detection captures undocumented or changed requirements.',
            bullets: [
              'Changes auto-mapped to parent systems',
              'Session-end hook maintains audit trail',
              'Integrates with the audit suite',
            ],
          },
          {
            name: 'Configuration Governance',
            description: 'File Purpose Policy enforces mutually exclusive config scopes.',
            bullets: [
              'Constitution for principles, CLAUDE.md for instructions, MEMORY.md for learned context',
              '`/dream` audits all three files for redundancy & spec drift',
              'Reports misplaced content & policy violations',
            ],
          },
          {
            name: 'Task Queue',
            description: 'Queue requirements during the day, run builds during off-peak hours.',
            bullets: [
              'Full lifecycle build & bugfix processing',
              'Scheduled off-peak batch processing',
              '`/smith-queue list` shows scheduled work',
            ],
          },
        ],
      },
      {
        id: 'agency',
        name: 'Agency',
        tagline: 'Project management, reporting',
        note: '(coming soon)',
        features: [
          {
            name: 'Audit Suite',
            description:
              'Specialized agents for security, performance, quality, SEO, & accessibility.',
            bullets: [
              'Specialized audits run in parallel sub-agents',
              'Consolidated severity-ranked action plan',
              'Requirements audit catches code drift',
              'Infrastructure audit checks Docker health, environment variables, & backups',
            ],
          },
          {
            name: 'Automated Timesheets',
            description: 'Generate audience-formatted timesheets across multiple projects.',
            bullets: [
              'Client, manager, internal, & accounting audience formats',
              'Pulls from vault session logs, git history, & spec artifacts',
              'Natural language descriptions generated from raw file change data',
              'Output as markdown, CSV, or JSON',
            ],
          },
          {
            name: 'Client Reports',
            description:
              'One-command project reports with executive summaries, decision logs, & spec changelogs.',
            bullets: [
              'Progress reports, decision logs, spec changelogs, or full audit reports',
              'LLM-generated narrative summaries suitable for client or stakeholder delivery',
              'Date range and project filtering',
              'Output as markdown or PDF',
            ],
          },
        ],
      },
    ],
  },

  comparison: {
    sectionHeading: 'How Smith compares',
    sectionSubheading:
      'Smith combines memory, learning, workflow, & agency tooling in a single infrastructure layer. Other tools specialize. Smith integrates.',
    footerNote: 'Last verified April 2026',
    legend: {
      yes: 'Fully supported',
      partial: 'Partial or limited',
      no: 'Not supported',
    },
  },

  attckCredibility: {
    sectionHeading: 'We can help',
    paragraphs: [
      'ATTCK is a New York-based digital agency established in 2014. Every feature in Smith comes from a real workflow problem solved while shipping client work.',
      'Need help automating your business? We design and build custom AI systems that streamline operations.',
    ],
    link: {
      label: 'Talk to us',
      href: ATTCK_URL,
      external: true,
    },
  },

  footer: {
    srHeading: 'Smith — Footer',
    copyright: '© 2026 ATTCK. All rights reserved.',
    tagline: 'Claude Code Infrastructure for Agencies.',
    links: [
      {
        label: 'GitHub',
        href: GITHUB_URL,
        external: true,
      },
      {
        label: 'ATTCK',
        href: ATTCK_URL,
        external: true,
      },
    ],
  },
};

export default content;
